import { Dayjs } from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { Snackbar, SnackbarCloseReason } from '@mui/material'
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import ConsumerCheckInTable from './Components/ConsumerCheckInTable'
import ConsumerProfileDetails from './Components/ConsumerProfileDetails'
import { ChangeSupervisorModal } from './Components/ChangeSupervisorModal'
import FilterModal from '@/components/FilterModal'
import Loader from '@/components/Loader'
import TaskModal from '@/components/Modal'
import DatePickerModal from './Components/DatePickerModal'
import StyledBgContainer from '@/components/StyledBgContainer/StyledBgContainer'
import useConsumerDetails from '@/services/hooks/useConsumerDetails'
import useCheckInCount from '@/services/hooks/useCheckInCount'
import useBlockConsumer from '@/services/hooks/useBlockConsumer'
import useFaceAuthentication from '@/services/hooks/useFaceAuthentication'
import { useParticipantSupervisor } from '@/services/hooks/useParticipantSupervisor'
import useLocalStorage from '@/hooks/useLocalStorage'
import {
  ConsumerUser,
  PaginatedResults,
  Submission,
} from '@/services/types/APIResponseType'
import { SupervisorInfo } from '@/services/index'
import { getDateRange } from '@/utils'
import { TimePeriod } from '@/utils/config/user-enums'
import Path from '@/router/localRoutes'
import './RegisteredConsumerDetails.css'

interface ActiveFilter {
  startDate: string
  endDate: string
}

const RegisteredConsumerDetails = () => {
  const itemPerPage = 10
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [consumerUser, setConsumerUser] = useState<ConsumerUser>()
  const [checkInData, setCheckInData] = useState<PaginatedResults[]>()
  const [checkTotalCount, setCheckTotalCount] = useState<number>()
  const [open, setOpen] = useState(false)
  const [openDate, setOpenDate] = useState(false)
  const [openChangeSupervisor, setOpenChangeSupervisor] = useState(false)
  const [toast, setToast] = useState<boolean>()
  const [error, setError] = useState<string>()
  const [isDownload, setIsDownload] = useState<boolean>(false)
  const [activeFilter, setActiveFilter] = useState(TimePeriod.ALL)
  const [supervisor, setSupervisor] = useState<SupervisorInfo | null>(null)
  const [localConsumerId] = useLocalStorage('ConsumerId')
  const [searchParams] = useSearchParams()
  const pageFrom = searchParams.get('from') ?? ''
  const consumerIdFromParams = searchParams.get('consumerID')
  const navigate = useNavigate()

  const consumerId = consumerIdFromParams
    ? consumerIdFromParams
    : localConsumerId

  const { mutate: consumersDetails, isPending } = useConsumerDetails()
  const { mutate: checkInCount, isPending: CheckInPending } = useCheckInCount()
  const { mutate: faceAuthentication, isPending: faceAuthPending } =
    useFaceAuthentication()
  const { mutate: blockConsumer } = useBlockConsumer()

  const {
    data: supervisorData,
    isLoading: isSupervisorLoading,
    refetch: refetchSupervisor,
  } = useParticipantSupervisor(consumerId ?? undefined)

  useEffect(() => {
    if (supervisorData?.data?.supervisor !== undefined) {
      setSupervisor(supervisorData.data.supervisor)
    }
  }, [supervisorData])

  const fetchConsumersDetails = useCallback(
    (requestData: ActiveFilter) => {
      checkInCount(
        {
          consumerId: consumerId,
          ...requestData,
        },
        {
          onSuccess: (response) => {
            setConsumerUser(response?.data)
          },
        },
      )
    },
    [checkInCount, consumerId],
  )

  const fetchConsumerCheckIn = useCallback(
    (requestData: ActiveFilter) => {
      consumersDetails(
        {
          consumerId: consumerId,
          limit: itemPerPage,
          page: currentPage,
          ...requestData,
        },
        {
          onSuccess: (response) => {
            setCheckInData(response?.data?.paginatedResults)
            setCheckTotalCount(response.data.totalCount)
          },
          onError: (error) => {
            console.log(error)
          },
        },
      )
    },
    [consumersDetails, consumerId, itemPerPage, currentPage],
  )

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleDateOpen = () => setOpenDate(true)
  const handleDateClose = () => setOpenDate(false)

  const getFormattedAddress = (item: Submission) => {
    let address = ''
    if (item?.street_number) {
      address += item?.street_number + ','
    }
    if (item?.route) {
      address += item?.route + ','
    }
    if (item?.neighborhood) {
      address += item?.neighborhood + ','
    }
    if (item?.sublocality_level_1) {
      address += item?.sublocality_level_1 + ','
    }
    if (item?.sublocality_level_2) {
      address += item?.sublocality_level_2 + ','
    }
    if (item?.city) {
      address += item?.city + ','
    }
    if (item?.state) {
      address += item?.state + ','
    }
    if (item?.country) {
      address += item?.country
    }

    return address
  }

  const handleBack = () => {
    if (pageFrom === 'baby-sitter') {
      return `${Path.AdminDashboard}?tab=reg-supervisor`
    } else if (pageFrom === 'random-check-ins') {
      return `${Path.AdminDashboard}?tab=random-check-ins`
    } else {
      return `${Path.AdminDashboard}?tab=reg-participant`
    }
  }

  const handleFilterChange = (filter: TimePeriod) => {
    setActiveFilter(filter)
  }
  const handleProfile = () => {
    if (consumerUser?.profileDetails?.profilePic) {
      navigate({
        pathname: Path.Profile,
        search: createSearchParams({
          image: consumerUser?.profileDetails?.profilePic,
        }).toString(),
      })
    }
  }

  const handleFaceVerify = (submissions_id: string) => {
    faceAuthentication(
      { id: submissions_id },
      {
        onSuccess: () => {
          fetchConsumerCheckIn(getDateRange(activeFilter))
        },
        onError: (error) => {
          console.log(error, 'error')
        },
      },
    )
  }

  const handleStatusChange = (isBlocked: boolean) => {
    const requestedData = {
      userId: consumerUser?.profileDetails.userId._id || '',
      isAccountBlocked: isBlocked,
    }

    if (requestedData.userId) {
      blockConsumer(requestedData)
    }
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const handleDownload = (
    selectedStartDate: Dayjs | null,
    selectedEndDate: Dayjs | null,
  ) => {
    if (!selectedStartDate || !selectedEndDate) {
      return
    }
    const formattedStartDate = selectedStartDate
      ? selectedStartDate.format('YYYY-MM-DD')
      : null
    const formattedEndDate = selectedEndDate
      ? selectedEndDate.format('YYYY-MM-DD')
      : null
    setIsDownload(true)
    consumersDetails(
      {
        consumerId: consumerId,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      },
      {
        onSuccess: (response) => {
          setIsDownload(false)
          if (
            !response?.data?.paginatedResults ||
            response?.data?.paginatedResults.length === 0
          ) {
            setToast(true)
            setError('No data available for the selected date range.')
            return
          }

          const csvRows = []
          csvRows.push(
            ['Time', 'Check-ins', 'Location', 'Verified', 'Scanned Face'].join(
              ',',
            ),
          )

          const startDate = selectedStartDate.startOf('day').toDate()
          const endDate = selectedEndDate.endOf('day').toDate()

          const groupedSubmissions: Record<string, string[]> = {}

          response?.data?.paginatedResults.forEach((data: PaginatedResults) => {
            const checkInDate = new Date(data.date)

            if (checkInDate >= startDate && checkInDate <= endDate) {
              const dateKey = checkInDate.toLocaleDateString()
              if (!groupedSubmissions[dateKey]) {
                groupedSubmissions[dateKey] = []
              }

              data.submissions.forEach((item: Submission) => {
                const submissionDate = new Date(item.date)
                if (submissionDate >= startDate && submissionDate <= endDate) {
                  groupedSubmissions[dateKey].push(
                    [
                      item.time,
                      item.authentication,
                      getFormattedAddress(item),
                      item.authentication,
                      item.capturedImage,
                    ].join(','),
                  )
                }
              })
            }
          })

          Object.keys(groupedSubmissions).forEach((date) => {
            csvRows.push(`\n${date}`)
            csvRows.push(...groupedSubmissions[date])
          })

          const isEmpty = (obj: object) => Object.keys(obj).length === 0

          if (isEmpty(groupedSubmissions)) {
            setToast(true)
            setError('No data available for the selected date range.')
            return
          }

          const csvString = csvRows.join('\n')
          const blob = new Blob([csvString], { type: 'text/csv' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = 'checkInData.csv'
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          handleDateClose()
        },
        onError: (error) => {
          setIsDownload(false)
          setError(error.message || 'Something went wrong')
        },
      },
    )
  }

  const handleToastClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return
    }

    setToast(false)
  }

  useEffect(() => {
    fetchConsumersDetails(getDateRange(activeFilter))
    fetchConsumerCheckIn(getDateRange(activeFilter))
  }, [activeFilter, fetchConsumersDetails, fetchConsumerCheckIn])

  return (
    <StyledBgContainer>
      <div className="reg-consumer-main-container ">
        <div className="reg-consumer-header font-sans font-semibold text-[24px]">
          <div onClick={() => navigate(handleBack())}>
            <img src="/icon/Arrow left.svg" alt="Back Arrow" />
          </div>
          <label>Details</label>
          <label></label>
        </div>
        {isPending && !isDownload ? (
          <Loader />
        ) : (
          <ConsumerProfileDetails
            handleProfile={handleProfile}
            consumersDetails={consumerUser!}
            handleFilterOpen={handleOpen}
            checkInData={checkInData!}
            handleUserStatusChange={handleStatusChange}
            handleDateOpen={handleDateOpen}
            supervisor={supervisor}
            isSupervisorLoading={isSupervisorLoading}
            onChangeSupervisor={() => {
              console.log(
                '[RegisteredConsumerDetails] onChangeSupervisor called, setting openChangeSupervisor=true',
              )
              setOpenChangeSupervisor(true)
            }}
          />
        )}
        <FilterModal
          open={open}
          handleClose={handleClose}
          activeFilter={activeFilter}
          handleFilterChange={handleFilterChange}
        />

        {openDate && (
          <TaskModal
            content={
              <DatePickerModal
                openDate={openDate}
                handleDateClose={handleDateClose}
                handleDownload={handleDownload}
                handlePending={isPending}
              />
            }
            open={openDate}
            handleClose={handleClose}
          />
        )}

        {
          console.log(
            '[RegisteredConsumerDetails] render — openChangeSupervisor:',
            openChangeSupervisor,
            'consumerId:',
            consumerId,
          ) as unknown as null
        }
        {openChangeSupervisor && (
          <ChangeSupervisorModal
            open={openChangeSupervisor}
            consumerId={consumerId ?? ''}
            currentSupervisor={supervisor}
            onClose={() => setOpenChangeSupervisor(false)}
            onSuccess={(newSupervisor) => {
              setSupervisor(newSupervisor)
              refetchSupervisor()
            }}
          />
        )}

        <Snackbar
          key={'top' + 'center'}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          autoHideDuration={2000}
          onClose={handleToastClose}
          open={toast}
          message={error}
        />

        <ConsumerCheckInTable
          checkInTableData={checkInData!}
          CheckInPending={CheckInPending}
          handleFaceVerify={handleFaceVerify}
          faceAuthPending={faceAuthPending}
          currentPage={currentPage}
          itemPerPage={itemPerPage}
          totalItemCount={checkTotalCount!}
          handlePageChange={handlePageChange}
        />
      </div>
    </StyledBgContainer>
  )
}

export default RegisteredConsumerDetails
